'use client';

import { useEffect, useRef } from 'react';
import { Button } from '@extern/ui/button';
import { Checkbox } from '@extern/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { InputPassword, InputPasswordTooltip } from '@extern/ui/input-password';
import { LinkButton } from '@extern/ui/link-button';
import { Separator } from '@extern/ui/separator';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { AuthGoogleButton } from '@/app/(auth)/_partials/auth-google-button';
import { AuthLastUsedButtonWrap } from '@/app/(auth)/_partials/auth-last-used-badge';
import { emailSchema, passwordSchema } from '@/infra/schemas/auth';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { useLastAuthenticationStrategy } from '../../_hooks/use-last-authentication-strategy';
import { useSignUp } from '../_hooks/use-sign-up';
import { useSignUpFlowStore } from '../_hooks/use-sign-up-flow-store';
const FormSchema = z.object({
  firstName: z.string().min(2, {
    message: 'First name is required'
  }),
  lastName: z.string().min(2, {
    message: 'Last name is required'
  }),
  emailAddress: emailSchema,
  password: passwordSchema
});
export function SignUpFormExperiment() {
  const searchParams = useSearchParams();
  const isFormVisible = useSignUpFlowStore(s => s.isStartFormVisible);
  const newsletter = useSignUpFlowStore(s_0 => s_0.newsletter);
  const startFormDraft = useSignUpFlowStore(s_1 => s_1.startFormDraft);
  const onChangeFlow = useSignUpFlowStore(s_2 => s_2.onChange);
  const {
    isPending,
    isGoogleLoading,
    onSignUpSubmit,
    onSignUpWithGoogle
  } = useSignUp();
  const {
    trackAuthScreenViewed,
    trackSignUpChoiceClicked,
    trackSignUpEmailReturnClicked
  } = useAuthEvents();
  const lastAuthenticationStrategy = useLastAuthenticationStrategy();
  const lastTrackedScreenRef = useRef<string | null>(null);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: startFormDraft,
    mode: 'onChange'
  });
  useEffect(() => {
    const subscription = form.watch(values => {
      onChangeFlow({
        startFormDraft: {
          firstName: values.firstName ?? '',
          lastName: values.lastName ?? '',
          emailAddress: values.emailAddress ?? '',
          password: values.password ?? ''
        }
      });
    });
    return () => subscription.unsubscribe();
  }, [form, onChangeFlow]);
  useEffect(() => {
    const currentScreen = isFormVisible ? 'start_email_form' : 'start';
    if (lastTrackedScreenRef.current === currentScreen) {
      return;
    }
    lastTrackedScreenRef.current = currentScreen;
    trackAuthScreenViewed('sign_up', currentScreen, 'start');
  }, [isFormVisible, trackAuthScreenViewed]);
  return <div className="flex w-full flex-col gap-8">
      <Form {...form}>
        <form className="flex w-full max-w-full flex-col items-center gap-6 overflow-visible rounded-3xl border border-role-outline-subtle bg-white p-6 sm:w-[358px]" onSubmit={form.handleSubmit(values_0 => {
        trackSignUpChoiceClicked('email', newsletter);
        onSignUpSubmit({
          ...values_0,
          newsletter
        });
      })}>
          {isFormVisible ? <div className="flex w-full flex-col gap-4">
              <FormField control={form.control} name="firstName" render={({
            field
          }) => <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input placeholder="E.g. John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="lastName" render={({
            field: field_0
          }) => <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input placeholder="E.g. Doe" {...field_0} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="emailAddress" render={({
            field: field_1
          }) => <FormItem>
                    <FormLabel>Your email (personal, work or edu)</FormLabel>
                    <FormControl>
                      <Input placeholder="email@example.com" {...field_1} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={form.control} name="password" render={({
            field: field_2
          }) => <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Create a password
                      <InputPasswordTooltip value={field_2.value} />
                    </FormLabel>
                    <FormControl>
                      <InputPassword autoCapitalize="none" autoComplete="password" autoCorrect="off" data-cy="password" id="password" key="password" placeholder="Type in your password" type="password" {...field_2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <div className="flex gap-2">
                <Button appearance="outlined" className="flex-1" color="info" disabled={isPending} onClick={() => {
              trackSignUpEmailReturnClicked('start_email_form', 'start');
              onChangeFlow({
                isStartFormVisible: false
              });
            }} type="button">
                  Return
                </Button>
                <Button appearance="filled" className="flex-1" color="info" disabled={isPending || !form.formState.isValid} isLoading={isPending} type="submit">
                  Continue
                </Button>
              </div>
            </div> : <>
              <AuthGoogleButton isLoading={isGoogleLoading} label="Sign up with Google" onClick={() => {
            trackSignUpChoiceClicked('google', newsletter);
            onSignUpWithGoogle(newsletter).catch(() => undefined);
          }} />

              <div className="relative flex w-full items-center justify-center p-3">
                <Separator />
                <span className="absolute mx-auto bg-white px-4 text-gray-dark text-sm">or</span>
              </div>

              <AuthLastUsedButtonWrap matchStrategy="password" strategy={lastAuthenticationStrategy}>
                <Button appearance="outlined" className="w-full" color="info" onClick={() => {
              trackSignUpChoiceClicked('email', newsletter);
              onChangeFlow({
                isStartFormVisible: true
              });
            }} type="button">
                  Sign up with Email
                </Button>
              </AuthLastUsedButtonWrap>

              <Checkbox checked={newsletter} label="I want exclusive invites and opportunities at Extern" onCheckedChange={checked => onChangeFlow({
            newsletter: checked === true
          })} />

              <Separator />

              <Text className="text-center" label="md">
                By signing up, you agree to Extern&apos;s{' '}
                <Link className="font-medium underline" href="https://www.extern.com/terms-of-service" target="_blank">
                  Terms
                </Link>{' '}
                and{' '}
                <Link className="font-medium underline" href="https://www.extern.com/privacy-policy" target="_blank">
                  Privacy Policy.
                </Link>
              </Text>
            </>}
        </form>
      </Form>
      {isFormVisible ? null : <LinkButton appearance="ghost" className="gap-0" color="neutral" href={`/sign-in?${getAllowlistedSearchParams(searchParams)}`}>
          Already have an account?&nbsp;<span className="text-role-info">Sign in</span>
        </LinkButton>}
    </div>;
}